import { HOMETREE_PHONE_NUMBER } from 'src/constants';
import { determineJobUrgency } from 'src/utils/jobs';
import { CustomerData } from 'src/types/CustomerData';
import { AreaOfIssue } from 'src/types/Job';
import { ProductEnum } from 'src/types/ProductEnum';
import Button from 'src/components/Button';
import { FormState, Reattend } from '../types/Form';
import { QuestionType, QuestionStage } from '../types/Question';
import { boilerQuestions } from './boilerQuestions';
import { heatingQuestions } from './heatingQuestions';
import { electricQuestions } from './electricQuestions';
import { windowsQuestions } from './windowsQuestions';
import { locksQuestions } from './locksQuestions';
import { doorsQuestions } from './doorsQuestions';
import { pestsQuestions } from './pestsQuestions';
import { determinePaymentNeeded } from '../utils/determinePaymentNeeded';
import { plumbingQuestions } from './plumbingQuestions';
import { drainsQuestions } from './drainsQuestions';
import { hasOpenJobSameType } from '../utils/hasOpenJobSameType';
import { jobTypeMap } from '../data/jobTypeMap';

export const questionStages = (
    customerData: CustomerData,
    formState: Partial<FormState>,
    reattend: Reattend
): QuestionStage[] => {
    return [
        // Import job type specific questions
        ...boilerQuestions(customerData),
        ...heatingQuestions(customerData),
        ...electricQuestions(customerData),
        ...plumbingQuestions(customerData),
        ...drainsQuestions(customerData),
        ...windowsQuestions(customerData),
        ...locksQuestions(customerData),
        ...doorsQuestions(customerData),
        ...pestsQuestions(customerData),

        // Shared questions
        {
            handle: 'problemArea',
            questions: [
                {
                    name: 'selfFixText',
                    type: QuestionType.TEXT,
                    isRequired: true,
                    config: {
                        extraCopy: (
                            <>
                                <h1>Request a Repair</h1>
                                {/* <h2>A few other things you should know before you proceed.</h2> */}
                                <h3>
                                    Many problems can be quickly solved by yourself without an
                                    engineer visit
                                </h3>
                                <p>
                                    We have created self-help guides for the most common issues that
                                    our customers face. Before contacting us, we recommend giving
                                    these a try and you could be up and running in minutes without
                                    the need to wait for an engineer.
                                </p>
                                <div>
                                    <Button
                                        variant="primary"
                                        onClick={() =>
                                            window.open(
                                                'https://www.hometree.co.uk/energy-advice/boiler-cover/self-help-guide/',
                                                '_blank',
                                                'noopener noreferrer'
                                            )
                                        }
                                    >
                                        Try these quick fixes
                                    </Button>
                                </div>
                                <h3>
                                    Please answer the following questions below and we will be in
                                    touch very soon to confirm your repair booking.
                                </h3>
                            </>
                        ),
                    },
                },
                {
                    name: 'jobType',
                    description: 'What area of the home has the issue?',
                    type: QuestionType.RADIO,
                    isRequired: true,
                    config: {
                        options: [
                            { copy: 'Boiler', value: AreaOfIssue.BOILER },
                            { copy: 'Heating', value: AreaOfIssue.HEATING },
                            { copy: 'Electric', value: AreaOfIssue.ELECTRIC },
                            { copy: 'Plumbing', value: AreaOfIssue.PLUMBING },
                            { copy: 'Drainage', value: AreaOfIssue.DRAINAGE },
                            { copy: 'Windows', value: AreaOfIssue.WINDOWS },
                            { copy: 'Locks', value: AreaOfIssue.LOCKS },
                            { copy: 'Pests', value: AreaOfIssue.PESTS },
                            { copy: 'Doors', value: AreaOfIssue.DOORS },
                        ],
                    },
                },
                {
                    name: 'description',
                    description: `Please describe the fault fully`,
                    type: QuestionType.TEXTAREA,
                    config: {
                        placeholder:
                            'Please detail when the issue began, where the problem is and if there are any accessbility factors we should be aware of...',
                        text: {
                            fieldLength: 230,
                        },
                    },
                    isRequired: true,
                },
            ],
            onSubmit: (values) =>
                ({
                    jobType: values.jobType,
                    description: values.description,
                } as Partial<FormState>),
            options: [
                {
                    copy: 'Next',
                    nextQuestion: (values) => {
                        if (typeof values.jobType !== 'string') {
                            return 'policyNotCovered';
                        }

                        if (hasOpenJobSameType(customerData.jobs, values.jobType)) {
                            return 'sameJobTypeAlreadyOpen';
                        }

                        if (
                            values.jobType &&
                            typeof values.jobType === 'string' &&
                            customerData.includedProducts.includes(values.jobType as ProductEnum)
                        ) {
                            switch (values.jobType) {
                                case AreaOfIssue.BOILER:
                                    return 'boilerGroup1';
                                case AreaOfIssue.HEATING:
                                    return 'heatingGroup1';
                                case AreaOfIssue.ELECTRIC:
                                    return 'electricGroup1';
                                case AreaOfIssue.PLUMBING:
                                    return 'plumbingGroup1';
                                case AreaOfIssue.DRAINAGE:
                                    return 'drainageGroup1';
                                case AreaOfIssue.WINDOWS:
                                    return 'windowsGroup1';
                                case AreaOfIssue.LOCKS:
                                    return 'locksGroup1';
                                case AreaOfIssue.DOORS:
                                    return 'doorsGroup1';
                                case AreaOfIssue.PESTS:
                                    return 'pestsGroup1';
                                default:
                                    return 'policyNotCovered';
                            }
                        }

                        return 'policyNotCovered';
                    },
                },
            ],
        },
        {
            handle: 'vulnerability',
            questions: [
                {
                    name: 'vulnerable',
                    description:
                        'Please let us know if there are vulnerable people at the property?',
                    type: QuestionType.RADIO,
                    isRequired: true,
                    config: {
                        options: [
                            { copy: 'Yes', value: 'Yes' },
                            { copy: 'No', value: 'No' },
                        ],
                    },
                },
            ],
            onSubmit: (values: Partial<FormState>, submitFormState) => ({
                vulnerable: values.vulnerable,
                urgency: determineJobUrgency(
                    String(values.vulnerable),
                    submitFormState.problemType || '',
                    submitFormState.insecureProperty || '',
                    submitFormState.lockedOutFromProperty || '',
                    submitFormState.doorsSecureBuilding || ''
                ),
            }),
            options: [
                {
                    copy: 'Next',
                    nextQuestion: 'chooseDate',
                },
            ],
        },
        {
            handle: 'chooseDate',
            questions: [
                {
                    name: 'calendar',
                    description: 'Please let us know when you are free for the repair',
                    type: QuestionType.CALENDAR,
                    isRequired: true,
                    config: {},
                },
            ],
            onSubmit: (values) =>
                ({
                    dayOneDate: values.dayOneDate,
                    dayOneTime: values.dayOneTime,
                    dayTwoDate: values.dayTwoDate,
                    dayTwoTime: values.dayTwoTime,
                    dayThreeDate: values.dayThreeDate,
                    dayThreeTime: values.dayThreeTime,
                } as Partial<FormState>),
            options: [
                {
                    copy: 'Next',
                    nextQuestion: determinePaymentNeeded(customerData.contribution, reattend),
                },
            ],
        },
        {
            handle: 'paymentDetails',
            questions: [
                {
                    name: 'stripe',
                    description: 'Pay your contribution fee',
                    type: QuestionType.PAYMENT,
                    contribution: customerData.contribution,
                    isRequired: false,
                },
            ],
            onSubmit: (values) =>
                ({
                    paymentId: values.paymentId,
                } as Partial<FormState>),
            config: {
                hideOptionsButtons: true,
            },
            options: [
                {
                    copy: 'Create claim!',
                    nextQuestion: 'submitClaim',
                },
            ],
        },
        {
            handle: 'submitClaim',
            questions: [
                {
                    name: 'submitClaim',
                    type: QuestionType.STAGE_LOADER,
                },
            ],
            onSubmit: () => ({} as Partial<FormState>),
            config: {},
        },
        {
            handle: 'policyNotCovered',
            questions: [
                {
                    name: 'failed',
                    description: `It looks like your current plan doesn’t cover the fault you have described.`,
                    type: QuestionType.FAILED,
                    isRequired: true,
                    config: {
                        extraCopy: (
                            <span>
                                To upgrade your policy, please contact our team on&nbsp;
                                <a href={`tel:${HOMETREE_PHONE_NUMBER}`}>{HOMETREE_PHONE_NUMBER}</a>
                            </span>
                        ),
                    },
                },
            ],
            onSubmit: () => {},
            config: {},
        },
        {
            handle: 'lossOfServiceNotCovered',
            questions: [
                {
                    name: 'failed',
                    description: `Your current plan is an emergency cover policy. This means we can only send an engineer when there's a complete loss of service.`,
                    type: QuestionType.FAILED,
                    isRequired: true,
                    config: {
                        extraCopy: (
                            <span>
                                For more information on our products or to upgrade your policy
                                please contact&nbsp;
                                <a href={`tel:${HOMETREE_PHONE_NUMBER}`}>{HOMETREE_PHONE_NUMBER}</a>
                            </span>
                        ),
                    },
                },
            ],
            onSubmit: () => {},
            config: {},
        },
        {
            handle: 'sameJobTypeAlreadyOpen',
            questions: [
                {
                    name: 'failed',
                    description: `We have already received your request to repair your ${
                        formState.jobType ? jobTypeMap[formState.jobType] : '"Not Specified"'
                    }`,
                    type: QuestionType.FAILED,
                    isRequired: true,
                    config: {
                        extraCopy: (
                            <span>
                                If you are having issues then call us on &nbsp;
                                <a href={`tel:${HOMETREE_PHONE_NUMBER}`}>{HOMETREE_PHONE_NUMBER}</a>
                            </span>
                        ),
                    },
                },
            ],
            onSubmit: () => {},
            config: {},
        },
        {
            handle: 'optionNotCovered',
            questions: [
                {
                    name: 'failed',
                    type: QuestionType.FAILED,
                    isRequired: true,
                },
            ],
            onSubmit: () => {},
            config: {},
        },
    ];
};
